/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useState } from "react";
import { useApp } from "../../../context/AppContext";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { toast } from "../../ui/sonner";
import { Camera, X, Loader2, Trash2 } from "lucide-react";
import { inp, money } from "./assetUtils";

/* 申请报废：原因 + 照片 → CEO 审批（CEO 本人申请直接生效） */
export function AssetDisposeDialog({ zh, asset, onClose, onSaved }) {
  const { api, user } = useApp();
  const T = (z, e) => (zh ? z : e);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const isCeo = user.role === "ceo";
  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => f.type.startsWith("image/")).slice(0, 6 - files.length);
    if (arr.length) setFiles((p) => [...p, ...arr.map((f) => ({ f, url: URL.createObjectURL(f) }))]);
  };
  const submit = async () => {
    if (reason.trim().length < 2) return toast.error(T("请填写报废原因", "Enter a reason"));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", reason.trim());
      files.forEach((x) => fd.append("files", x.f));
      const { data } = await api.post(`/finance/fixed-assets/${asset.id}/dispose-request`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.autoApproved ? T("CEO 本人申请，资产已报废并停止折旧", "Disposed (CEO)") : T(`报废申请已提交，等待 CEO 审批（已通知 ${data.notified} 人）`, "Request submitted for CEO approval"));
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg" data-testid="asset-dispose-dialog">
      <DialogHeader><DialogTitle>{T("申请报废", "Request Disposal")}</DialogTitle>
        <DialogDescription><span className="font-semibold text-slate-700">{asset.name}</span> · {asset.id} · {T("原值", "Cost")} {money(asset.cost)}{asset.capitalized ? ` · ${T("净值", "NBV")} ${money(asset.netBook)}` : ""}</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <p className="text-xs text-slate-500 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="asset-dispose-hint">{isCeo ? T("您是 CEO：提交后直接生效，资产即报废并停止折旧。", "As CEO your request takes effect immediately.") : T("报废须经 CEO 审批。批准后资产状态变为「报废」并停止折旧；驳回则保持原状态。", "Disposal requires CEO approval; depreciation stops once approved.")}</p>
        <label className="text-sm text-slate-600 block">{T("报废原因", "Reason")} *<textarea className={`${inp} min-h-[72px]`} data-testid="asset-dispose-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={T("如：主板损坏无法维修，维修报价超过残值", "e.g. motherboard failure, repair cost exceeds NBV")} /></label>
        <div>
          <p className="text-sm text-slate-600">{T("现场照片（最多 6 张）", "Photos (up to 6)")}</p>
          <div className="mt-1 flex flex-wrap gap-2" data-testid="asset-dispose-photos">
            {files.map((x, i) => (
              <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden border border-slate-200" data-testid={`asset-dispose-photo-${i}`}>
                <img src={x.url} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5" data-testid={`asset-dispose-photo-rm-${i}`}><X className="h-3 w-3" /></button>
              </div>
            ))}
            {files.length < 6 && (
              <label className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-indigo-400 flex flex-col items-center justify-center text-slate-400 text-[11px] cursor-pointer" data-testid="asset-dispose-photo-add">
                <Camera className="h-5 w-5" />{T("拍照 / 选图", "Add")}
                <input type="file" accept="image/*" multiple capture="environment" className="hidden" data-testid="asset-dispose-photo-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              </label>
            )}
          </div>
        </div>
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2"><Button variant="outline" onClick={onClose}>{T("取消", "Cancel")}</Button>
          <Button data-testid="asset-dispose-submit" disabled={busy} className="bg-red-600 hover:bg-red-700 gap-1.5" onClick={submit}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}{isCeo ? T("确认报废", "Dispose now") : T("提交 CEO 审批", "Submit for approval")}</Button></div>
      </div>
    </DialogContent></Dialog>
  );
}
